/**
 * Role utility functions for consistent permission checking
 * Handles both legacy (lowercase) and new (capitalized) role names
 */

// Admin roles (both legacy and new)
const ADMIN_ROLES = ['Admin', 'admin'];

// HSE Manager roles (both legacy and new)
const HSE_MANAGER_ROLES = ['HSE Manager', 'hse_manager'];

// Safety Manager roles
const SAFETY_MANAGER_ROLES = ['Safety Manager'];

// All management roles
const MANAGEMENT_ROLES = [...ADMIN_ROLES, ...HSE_MANAGER_ROLES, ...SAFETY_MANAGER_ROLES, 'Manager', 'Director'];

/**
 * Check if user has admin role
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  return user?.role && ADMIN_ROLES.includes(user.role);
};

/**
 * Check if user has HSE Manager role
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const isHSEManager = (user) => {
  return user?.role && HSE_MANAGER_ROLES.includes(user.role);
};

/**
 * Check if user has Safety Manager role
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const isSafetyManager = (user) => {
  return user?.role && SAFETY_MANAGER_ROLES.includes(user.role);
};

/**
 * Check if user has admin or HSE Manager role
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const isAdminOrHSEManager = (user) => {
  return isAdmin(user) || isHSEManager(user);
};

/**
 * Check if user has any management role (admin, HSE manager, safety manager, etc.)
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const isManagement = (user) => {
  return user?.role && MANAGEMENT_ROLES.includes(user.role);
};

/**
 * Check if user can approve documents/workflows
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const canApprove = (user) => {
  return isAdmin(user) || isHSEManager(user) || isSafetyManager(user);
};

/**
 * Check if user can manage users
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const canManageUsers = (user) => {
  return isAdmin(user);
};

/**
 * Check if user can access admin settings
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const canAccessAdminSettings = (user) => {
  return isAdmin(user) || isHSEManager(user);
};

export default {
  isAdmin,
  isHSEManager,
  isSafetyManager,
  isAdminOrHSEManager,
  isManagement,
  canApprove,
  canManageUsers,
  canAccessAdminSettings,
  ADMIN_ROLES,
  HSE_MANAGER_ROLES,
  SAFETY_MANAGER_ROLES,
  MANAGEMENT_ROLES
};
